/**
 * Money helpers -- the cash desk speaks in integer CENTIMES (DZD) on the wire and never carries a
 * float across the API boundary (rounding-safe, medico-legal). These helpers convert between the
 * centimes integer and the display string / the editable major-unit string.
 *
 * Display is locale-aware but the numeric face stays mono + LTR (an "nv-ltr-island" inside an RTL
 * document) so amounts read identically in FR and AR.
 */

export const CURRENCY = "DZD";

/** Narrow no-break space (U+202F) -- the digit-group separator. Built from a codepoint (no literal). */
const GROUP_SEP = String.fromCharCode(0x202f);
/** Strips spaces a user may type/paste: ASCII space, NBSP (U+00A0), narrow NBSP (U+202F), other \s. */
const STRIP_WS = new RegExp("[" + String.fromCharCode(0x20, 0xa0, 0x202f) + "\\s]", "g");

/** Centimes (int) -> display string, e.g. 123450 -> "1 234,50 DZD". */
export function formatCentimes(centimes: number | null | undefined, withCurrency = true): string {
  if (centimes == null || !Number.isFinite(centimes)) return "—";
  const sign = centimes < 0 ? "-" : "";
  const abs = Math.abs(Math.round(centimes));
  const major = Math.floor(abs / 100);
  const minor = abs % 100;
  const groupedMajor = groupThousands(major);
  const body = `${sign}${groupedMajor},${String(minor).padStart(2, "0")}`;
  return withCurrency ? `${body} ${CURRENCY}` : body;
}

/** Insert the group separator every 3 digits from the right. */
function groupThousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP);
}

/**
 * Parse an editable major-unit string ("1234,50" or "1234.5") into integer centimes.
 * Returns null on an unparseable / empty input so callers can surface a field error.
 */
export function parseToCentimes(input: string): number | null {
  const trimmed = input.trim().replace(STRIP_WS, "");
  if (trimmed === "") return null;
  const normalized = trimmed.replace(",", ".");
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/** Centimes (int) -> bare editable major string ("1234,50"), for prefilling an input. */
export function centimesToEditable(centimes: number | null | undefined): string {
  if (centimes == null || !Number.isFinite(centimes)) return "";
  const abs = Math.abs(Math.round(centimes));
  const major = Math.floor(abs / 100);
  const minor = abs % 100;
  return minor === 0 ? String(major) : `${major},${String(minor).padStart(2, "0")}`;
}

/** Sum a list of centimes lines. */
export function sumCentimes(values: ReadonlyArray<number>): number {
  return values.reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
}
